import { z } from "zod";
import { asTripId, ok, TRIP_COLORS } from "@pack-it-up/shared";
import { RoleSchema, VisibilitySchema, calendarDate, firestoreTimestamp, parseDto, toTimestamp, } from "./common.js";
// Sticky list filters — optional; a doc written before this field existed just
// parses as undefined and the store falls back to its own defaults. `sort`/
// `filterTags` got `.default(...)` fallbacks since a doc written by the earlier
// version of this feature (assignee/status only) still needs to parse cleanly.
const StickyListFilterSchema = z.object({
    filterAssignees: z.union([z.array(z.string()), z.literal("all")]),
    filterStatus: z.enum(["all", "complete", "incomplete"]),
    sort: z.enum(["manual", "alphabetical", "dueDate"]).default("manual"),
    filterTags: z.array(z.string()).default([]),
});
// T27 — a doc written before staging existed has no `settings` at all; `.default(...)`
// fills it in so the domain's non-optional Trip.settings/MemberSettings always parse.
const MemberSettingsSchema = z
    .object({
    stagingOptIn: z.boolean().default(false),
    completedToBottom: z.boolean().optional(),
    templateDisplay: z.enum(["tag", "highlight"]).optional(),
    itemsFilter: StickyListFilterSchema.optional(),
    todosFilter: StickyListFilterSchema.optional(),
})
    .default({ stagingOptIn: false });
const TripSettingsSchema = z
    .object({
    allowProxyCompletion: z.boolean().default(false),
})
    .default({ allowProxyCompletion: false });
const MemberSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("user"),
        role: RoleSchema,
        profile: z.object({ displayName: z.string(), email: z.string(), photoUrl: z.string().optional() }),
        settings: MemberSettingsSchema,
        // T31 — set only on anonymous "guest" members, tying them to the share link that
        // admitted them (revoking it removes them).
        guest: z.object({ linkToken: z.string() }).optional(),
    }),
    z.object({
        kind: z.literal("managed"),
        guardianUserIds: z.array(z.string()),
        profile: z.object({ displayName: z.string(), photoUrl: z.string().optional() }),
        settings: MemberSettingsSchema,
    }),
]);
export const TripDtoSchema = z.object({
    id: z.string(),
    name: z.string().min(1),
    startDate: calendarDate().optional(),
    endDate: calendarDate().optional(),
    location: z.string().optional(),
    description: z.string().optional(),
    color: z.enum(TRIP_COLORS).optional(),
    members: z.record(z.string(), MemberSchema),
    memberUserIds: z.array(z.string()),
    visibility: VisibilitySchema,
    settings: TripSettingsSchema,
    importedTemplateIds: z.array(z.string()),
    archived: z.boolean(),
    createdAt: firestoreTimestamp(),
});
const memberToDomain = (m) => {
    if (m.kind === "user") {
        return {
            kind: "user",
            role: m.role,
            profile: {
                displayName: m.profile.displayName,
                email: m.profile.email,
                ...(m.profile.photoUrl !== undefined ? { photoUrl: m.profile.photoUrl } : {}),
            },
            settings: m.settings,
            ...(m.guest !== undefined ? { guest: m.guest } : {}),
        };
    }
    return {
        kind: "managed",
        guardianUserIds: m.guardianUserIds,
        profile: {
            displayName: m.profile.displayName,
            ...(m.profile.photoUrl !== undefined ? { photoUrl: m.profile.photoUrl } : {}),
        },
        settings: m.settings,
    };
};
export const toDomainTrip = (raw) => {
    const p = parseDto(TripDtoSchema, raw, "Trip");
    if (!p.ok)
        return p;
    const d = p.value;
    const members = {};
    for (const [pid, m] of Object.entries(d.members)) {
        members[pid] = memberToDomain(m);
    }
    const trip = {
        id: asTripId(d.id),
        name: d.name,
        ...(d.startDate !== undefined ? { startDate: d.startDate } : {}),
        ...(d.endDate !== undefined ? { endDate: d.endDate } : {}),
        ...(d.location !== undefined ? { location: d.location } : {}),
        ...(d.description !== undefined ? { description: d.description } : {}),
        ...(d.color !== undefined ? { color: d.color } : {}),
        members,
        visibility: d.visibility,
        settings: d.settings,
        importedTemplateIds: d.importedTemplateIds,
        archived: d.archived,
        createdAt: d.createdAt,
    };
    return ok(trip);
};
// memberUserIds is the queryable mirror — only `user`-kind keys (Doc 02 §9).
export const userMemberIds = (members) => Object.keys(members).filter((k) => members[k]?.kind === "user");
export const toTripDto = (t) => ({
    id: t.id,
    name: t.name,
    ...(t.startDate !== undefined ? { startDate: t.startDate } : {}),
    ...(t.endDate !== undefined ? { endDate: t.endDate } : {}),
    ...(t.location !== undefined ? { location: t.location } : {}),
    ...(t.description !== undefined ? { description: t.description } : {}),
    ...(t.color !== undefined ? { color: t.color } : {}),
    members: t.members,
    memberUserIds: userMemberIds(t.members),
    visibility: t.visibility,
    settings: t.settings,
    importedTemplateIds: t.importedTemplateIds,
    archived: t.archived,
    createdAt: toTimestamp(t.createdAt),
});
//# sourceMappingURL=trip.mapper.js.map